import { FC, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Offcanvas,
  Row,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import type { ManagementTableRow, ManagementTableStore } from './management-table-import';
import {
  buildSchedulerAdminCoverageSummary,
  emptyLookupState,
  loadManagementTableStore,
  MANAGEMENT_TABLE_UPDATED_EVENT,
  toManagementRow,
  type ResourceLookupState,
} from './management-table-scaffold';
import {
  fetchAdminSettingsResource,
  type AdminSettingsOverviewResponse,
  type AdminSettingsReadinessResponse,
  type AdminSettingsResource,
  type AdminSettingsScheduleRecord,
} from './api';

type DynamicViewsWorkspaceProps = {
  cycleName: string | null;
  tenantQuery: string;
  readiness: AdminSettingsReadinessResponse | null;
  departmentHierarchy: AdminSettingsOverviewResponse['department_hierarchy'];
  unassignedScheduleGroups: AdminSettingsOverviewResponse['unassigned_schedule_groups'];
};

type ViewMode = 'table' | 'relationships' | 'overview' | 'readiness';
type ReadinessTone = 'critical' | 'warning' | 'info' | 'success';

type DetailRecord = {
  title: string;
  subtitle: string;
  lines: string[];
  route: string;
  actionLabel: string;
};

type ReadinessItem = {
  id: string;
  tone: ReadinessTone;
  title: string;
  body: string;
  route: string;
  actionLabel: string;
  locationNames: string[];
  serviceLines: string[];
};

type RelationshipGroup = {
  id: string;
  title: string;
  subtitle: string;
  records: Array<{
    id: string;
    title: string;
    subtitle: string;
    colorMeta?: {
      hex: string | null;
      label: string;
      textColor: '#111827' | '#ffffff';
    };
    badges: Array<{ label: string; tone: 'primary' | 'success' | 'warning' | 'info' }>;
    children: string[];
    locationNames: string[];
    serviceLines: string[];
    route: string;
    actionLabel: string;
  }>;
};

const viewModeLabels: Record<ViewMode, string> = {
  table: 'Table',
  relationships: 'Relationships',
  overview: 'Overview',
  readiness: 'Readiness',
};

const toneBadgeClassMap: Record<ReadinessTone | 'primary' | 'success' | 'warning' | 'info', string> = {
  critical: 'bg-danger-transparent text-danger',
  primary: 'bg-primary-transparent text-primary',
  success: 'bg-success-transparent text-success',
  warning: 'bg-warning-transparent text-warning',
  info: 'bg-info-transparent text-info',
};

const managementRoutes = {
  locations: '/admin-settings/locations',
  'schedule-types': '/admin-settings/schedule-types',
  'service-lines': '/admin-settings/service-lines',
  'groups-teams': '/admin-settings/groups-teams',
  'scheduler-admins': '/admin-settings/scheduler-admins',
} as const;

const normalizeValue = (value: string) => value.trim().toLowerCase();

const splitValues = (value: string | undefined) =>
  (value ?? '')
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);

const createEmptyStore = (): ManagementTableStore => ({
  locations: [],
  'schedule-types': [],
  'service-lines': [],
  'groups-teams': [],
  'scheduler-admins': [],
});

const colorHexRegex = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const expandHexColor = (value: string) => {
  const normalized = value.trim();

  if (!colorHexRegex.test(normalized)) {
    return null;
  }

  if (normalized.length === 4) {
    const [, red, green, blue] = normalized;
    return `#${red}${red}${green}${green}${blue}${blue}`.toUpperCase();
  }

  return normalized.toUpperCase();
};

const getChannelLuminance = (channel: number) => {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

const getContrastRatio = (leftHex: string, rightHex: string) => {
  const normalizedLeft = expandHexColor(leftHex);
  const normalizedRight = expandHexColor(rightHex);

  if (!normalizedLeft || !normalizedRight) {
    return 1;
  }

  const toRgb = (hex: string) => ({
    red: Number.parseInt(hex.slice(1, 3), 16),
    green: Number.parseInt(hex.slice(3, 5), 16),
    blue: Number.parseInt(hex.slice(5, 7), 16),
  });

  const getRelativeLuminance = ({ red, green, blue }: { red: number; green: number; blue: number }) =>
    (0.2126 * getChannelLuminance(red))
    + (0.7152 * getChannelLuminance(green))
    + (0.0722 * getChannelLuminance(blue));

  const left = getRelativeLuminance(toRgb(normalizedLeft));
  const right = getRelativeLuminance(toRgb(normalizedRight));
  const lighter = Math.max(left, right);
  const darker = Math.min(left, right);

  return (lighter + 0.05) / (darker + 0.05);
};

const getReadableTextColor = (hex: string) =>
  getContrastRatio(hex, '#FFFFFF') >= getContrastRatio(hex, '#111827') ? '#ffffff' : '#111827';

const getRecordColorMeta = (
  sectionId: 'locations' | 'service-lines' | 'groups-teams',
  row: ManagementTableRow,
  store: ManagementTableStore
) => {
  const manualColor = expandHexColor(row.cells.color ?? '');

  if (sectionId === 'locations') {
    return manualColor
      ? { hex: manualColor, label: 'Manual location color', textColor: getReadableTextColor(manualColor) as '#111827' | '#ffffff' }
      : null;
  }

  if (sectionId === 'service-lines') {
    if (manualColor) {
      return { hex: manualColor, label: 'Manual service-line color', textColor: getReadableTextColor(manualColor) as '#111827' | '#ffffff' };
    }

    const locationColors = Array.from(
      new Set(
        splitValues(row.cells.locations)
          .map((locationName) =>
            store.locations.find((location) => normalizeValue(location.cells.name ?? '') === normalizeValue(locationName))
          )
          .map((location) => expandHexColor(location?.cells.color ?? ''))
          .filter((value): value is string => Boolean(value))
      )
    );

    if (locationColors.length === 1) {
      return { hex: locationColors[0], label: 'Inherited from location', textColor: getReadableTextColor(locationColors[0]) as '#111827' | '#ffffff' };
    }

    return locationColors.length > 1
      ? { hex: null, label: 'Mixed location colors', textColor: '#111827' as const }
      : null;
  }

  if (manualColor) {
    return { hex: manualColor, label: 'Manual team color', textColor: getReadableTextColor(manualColor) as '#111827' | '#ffffff' };
  }

  const parentServiceLine = store['service-lines'].find((serviceLine) =>
    normalizeValue(serviceLine.cells.name ?? '') === normalizeValue(row.cells.serviceLine ?? '')
  );

  return parentServiceLine ? getRecordColorMeta('service-lines', parentServiceLine, store) : null;
};

const isManagedRow = (row: ManagementTableRow) => {
  const status = normalizeValue(row.status);
  return status === 'active' || status === 'draft' || status === 'pending';
};

const getSchedulerStatus = (row: ManagementTableRow) => normalizeValue(row.status) || 'pending';

const matchesSearch = (searchValue: string, values: string[]) => {
  if (!searchValue) {
    return true;
  }

  const haystack = values.join(' ').toLowerCase();
  return haystack.includes(searchValue);
};

const recordMatchesFilters = (
  searchValue: string,
  locationFilter: string,
  serviceLineFilter: string,
  values: string[],
  locationNames: string[],
  serviceLines: string[]
) => {
  if (!matchesSearch(searchValue, values)) {
    return false;
  }

  if (locationFilter !== 'all' && !locationNames.some((location) => normalizeValue(location) === locationFilter)) {
    return false;
  }

  if (serviceLineFilter !== 'all' && !serviceLines.some((serviceLine) => normalizeValue(serviceLine) === serviceLineFilter)) {
    return false;
  }

  return true;
};

const buildReadinessRouteMeta = (blocker: string) => {
  const normalized = blocker.toLowerCase();

  if (normalized.includes('scheduler admin')) {
    return {
      route: managementRoutes['scheduler-admins'],
      actionLabel: 'Review Scheduler Admin coverage',
    };
  }

  if (normalized.includes('location') || normalized.includes('hospital')) {
    return {
      route: managementRoutes.locations,
      actionLabel: 'Manage locations',
    };
  }

  if (normalized.includes('service line')) {
    return {
      route: managementRoutes['service-lines'],
      actionLabel: 'Manage service lines',
    };
  }

  if (normalized.includes('schedule')) {
    return {
      route: '/admin-settings/overview',
      actionLabel: 'Review schedule readiness',
    };
  }

  return {
    route: '/admin-settings/overview',
    actionLabel: 'Return to overview',
  };
};

const buildReadinessItems = (
  store: ManagementTableStore,
  schedulesByServiceLine: Record<string, string[]>,
  readiness: AdminSettingsReadinessResponse | null
): ReadinessItem[] => {
  const coverage = buildSchedulerAdminCoverageSummary(store);
  const locations = store.locations.filter(isManagedRow);
  const serviceLines = store['service-lines'].filter(isManagedRow);
  const schedulerAdmins = store['scheduler-admins'];
  const items: ReadinessItem[] = [];

  readiness?.company_activation_readiness.blockers.forEach((blocker, index) => {
    const routeMeta = buildReadinessRouteMeta(blocker);
    items.push({
      id: `activation-api-${index}`,
      tone: 'critical',
      title: 'Activation blocker',
      body: blocker,
      route: routeMeta.route,
      actionLabel: routeMeta.actionLabel,
      locationNames: [],
      serviceLines: [],
    });
  });

  readiness?.first_schedule_draft_readiness.blockers
    .filter((blocker) => !readiness.company_activation_readiness.blockers.includes(blocker))
    .forEach((blocker, index) => {
      const routeMeta = buildReadinessRouteMeta(blocker);
      items.push({
        id: `draft-api-${index}`,
        tone: 'warning',
        title: 'First-draft readiness gap',
        body: blocker,
        route: routeMeta.route,
        actionLabel: routeMeta.actionLabel,
        locationNames: [],
        serviceLines: [],
      });
    });

  serviceLines
    .filter((row) => !splitValues(row.cells.locations).length && normalizeValue(row.cells.locations ?? '') !== 'companywide')
    .forEach((row) => {
      items.push({
        id: `locations-${row.id}`,
        tone: 'critical',
        title: `${row.cells.name || 'Service line'} is missing location coverage`,
        body: 'Attach at least one hospital/location or mark the service line Companywide before continuing.',
        route: managementRoutes['service-lines'],
        actionLabel: 'Fix service-line scope',
        locationNames: [],
        serviceLines: [row.cells.name ?? ''],
      });
    });

  locations
    .filter((row) => splitValues(row.cells.serviceLines).length === 0)
    .forEach((row) => {
      items.push({
        id: `orphan-location-${row.id}`,
        tone: 'warning',
        title: `${row.cells.name || 'Location'} has no service lines`,
        body: 'Attach at least one service line so this location appears in coverage and readiness reporting.',
        route: managementRoutes.locations,
        actionLabel: 'Manage locations',
        locationNames: [row.cells.name ?? ''],
        serviceLines: [],
      });
    });

  schedulerAdmins
    .filter((row) => getSchedulerStatus(row) === 'pending')
    .forEach((row) => {
      items.push({
        id: `pending-${row.id}`,
        tone: 'warning',
        title: `${row.cells.name || 'Scheduler Admin'} invite is still pending`,
        body: `${row.cells.serviceLine || 'Assigned scope'} counts for activation coverage, but not for first-draft readiness until the user is active.`,
        route: managementRoutes['scheduler-admins'],
        actionLabel: 'Review assignment',
        locationNames: splitValues(row.cells.locationScope),
        serviceLines: [row.cells.serviceLine ?? ''],
      });
    });

  serviceLines
    .filter((row) => (schedulesByServiceLine[row.cells.name ?? ''] ?? []).length === 0)
    .forEach((row) => {
      items.push({
        id: `schedule-${row.id}`,
        tone: 'info',
        title: `${row.cells.name || 'Service line'} has no schedules yet`,
        body: 'No schedule rows are currently available for this service line.',
        route: '/admin-settings/overview',
        actionLabel: 'Review schedule readiness',
        locationNames: splitValues(row.cells.locations),
        serviceLines: [row.cells.name ?? ''],
      });
    });

  if (items.length === 0) {
    items.push({
      id: 'ready',
      tone: 'success',
      title: 'No readiness blockers are currently visible',
      body: 'Activation coverage, first-draft readiness, and current schedule rows all look clear in Company Admin Settings.',
      route: '/admin-settings/overview',
      actionLabel: 'Return to overview',
      locationNames: [],
      serviceLines: [],
    });
  }

  return items;
};

const DynamicViewsWorkspace: FC<DynamicViewsWorkspaceProps> = ({
  cycleName,
  tenantQuery,
  readiness,
  departmentHierarchy,
  unassignedScheduleGroups,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('relationships');
  const [searchValue, setSearchValue] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [serviceLineFilter, setServiceLineFilter] = useState('all');
  const [readinessFilter, setReadinessFilter] = useState('all');
  const [store, setStore] = useState<ManagementTableStore>(loadManagementTableStore);
  const [schedules, setSchedules] = useState<AdminSettingsScheduleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [detailRecord, setDetailRecord] = useState<DetailRecord | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadResources = async () => {
      setLoading(true);
      setRequestError(null);

      try {
        const resources: AdminSettingsResource[] = [
          'locations',
          'schedule-types',
          'service-lines',
          'teams',
          'scheduler-admin-scopes',
          'schedules',
        ];
        const responses = await Promise.all(
          resources.map((resourceKey) => fetchAdminSettingsResource(tenantQuery, resourceKey, { per_page: '100' }))
        );

        if (cancelled) {
          return;
        }

        const nextLookups = responses.reduce<ResourceLookupState>((accumulator, response, index) => {
          const resourceKey = resources[index];

          if (resourceKey === 'schedules') {
            return accumulator;
          }

          accumulator[resourceKey] = response.data.data as ResourceLookupState[typeof resourceKey];
          return accumulator;
        }, emptyLookupState());

        const nextStore = createEmptyStore();
        nextStore.locations = nextLookups.locations.map((record) => toManagementRow('locations', record, nextLookups));
        nextStore['schedule-types'] = nextLookups['schedule-types'].map((record) => toManagementRow('schedule-types', record, nextLookups));
        nextStore['service-lines'] = nextLookups['service-lines'].map((record) => toManagementRow('service-lines', record, nextLookups));
        nextStore['groups-teams'] = nextLookups.teams.map((record) => toManagementRow('groups-teams', record, nextLookups));
        nextStore['scheduler-admins'] = nextLookups['scheduler-admin-scopes'].map((record) => toManagementRow('scheduler-admins', record, nextLookups));

        setStore(nextStore);
        setSchedules((responses.find((response) => response.resource === 'schedules')?.data.data ?? []) as AdminSettingsScheduleRecord[]);
      } catch (error) {
        if (!cancelled) {
          setRequestError(error instanceof Error ? error.message : 'Unable to load dynamic Company Admin Settings views.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const handleStoreUpdate = () => {
      void loadResources();
    };

    void loadResources();
    window.addEventListener(MANAGEMENT_TABLE_UPDATED_EVENT, handleStoreUpdate);

    return () => {
      cancelled = true;
      window.removeEventListener(MANAGEMENT_TABLE_UPDATED_EVENT, handleStoreUpdate);
    };
  }, [tenantQuery]);

  const loweredSearch = searchValue.trim().toLowerCase();
  const hasCanonicalHierarchy = departmentHierarchy.length > 0 || unassignedScheduleGroups.length > 0;
  const canonicalCoverage = useMemo(() => {
    const scheduleGroups = departmentHierarchy.flatMap((department) => department.schedule_groups);
    const coveredGroups = scheduleGroups.filter((group) => group.effective_scheduler_admins.length > 0);

    return {
      departmentCount: departmentHierarchy.length,
      departmentAdminCount: departmentHierarchy.filter((department) => department.scheduler_admins.length > 0).length,
      scheduleGroupCount: scheduleGroups.length,
      coveredGroupCount: coveredGroups.length,
      uncoveredGroups: scheduleGroups.filter((group) => group.effective_scheduler_admins.length === 0),
      unassignedGroupCount: unassignedScheduleGroups.length,
    };
  }, [departmentHierarchy, unassignedScheduleGroups]);
  const locations = useMemo(() => store.locations.filter(isManagedRow), [store]);
  const serviceLines = useMemo(() => store['service-lines'].filter(isManagedRow), [store]);
  const groups = useMemo(() => store['groups-teams'].filter(isManagedRow), [store]);
  const schedulerAdmins = useMemo(() => store['scheduler-admins'], [store]);
  const schedulesByServiceLine = useMemo(() => {
    const scheduleMap = schedules.reduce<Record<string, string[]>>((accumulator, schedule) => {
      const serviceLineName = schedule.service_line?.name ?? '';

      if (!serviceLineName) {
        return accumulator;
      }

      if (!accumulator[serviceLineName]) {
        accumulator[serviceLineName] = [];
      }

      accumulator[serviceLineName].push(schedule.name);
      return accumulator;
    }, {});

    return Object.fromEntries(
      serviceLines.map((row) => {
        const serviceLineName = row.cells.name ?? '';
        return [serviceLineName, scheduleMap[serviceLineName] ?? []];
      })
    ) as Record<string, string[]>;
  }, [schedules, serviceLines]);
  const readinessItems = useMemo(() => buildReadinessItems(store, schedulesByServiceLine, readiness), [readiness, schedulesByServiceLine, store]);
  const coverage = useMemo(() => buildSchedulerAdminCoverageSummary(store), [store]);

  const locationOptions = useMemo(
    () => Array.from(new Set(locations.map((row) => row.cells.name ?? '').filter(Boolean))).sort((left, right) => left.localeCompare(right)),
    [locations]
  );
  const serviceLineOptions = useMemo(
    () => Array.from(new Set(serviceLines.map((row) => row.cells.name ?? '').filter(Boolean))).sort((left, right) => left.localeCompare(right)),
    [serviceLines]
  );

  const overviewCards = useMemo(() => {
    const totalSchedules = Object.values(schedulesByServiceLine).reduce((sum, schedules) => sum + schedules.length, 0);

    return [
      { label: 'Locations', value: `${locations.length}`, tone: 'primary' as const },
      { label: 'Service lines', value: `${serviceLines.length}`, tone: 'success' as const },
      { label: 'Departments / teams', value: `${groups.length}`, tone: 'info' as const },
      { label: 'Scheduler Admins', value: `${schedulerAdmins.length}`, tone: 'primary' as const },
      { label: 'Schedules', value: `${totalSchedules}`, tone: 'info' as const },
      { label: 'Open blockers', value: `${readinessItems.filter((item) => item.tone === 'critical').length}`, tone: 'warning' as const },
    ];
  }, [groups.length, locations.length, readinessItems, schedulesByServiceLine, schedulerAdmins.length, serviceLines.length]);

  const relationshipGroups = useMemo<RelationshipGroup[]>(() => {
    const canonicalHierarchyRecords: RelationshipGroup['records'] = departmentHierarchy
      .map((department) => {
        const departmentAdminNames = department.scheduler_admins.map((admin) => admin.name);
        const scheduleGroups = department.schedule_groups.map((group) => {
          const directAdminNames = group.direct_scheduler_admins.map((admin) => admin.name);
          const inheritedAdminNames = departmentAdminNames.filter((name) => !directAdminNames.includes(name));
          const coverage = [
            directAdminNames.length
              ? `Direct Scheduler Admin: ${directAdminNames.join(', ')}`
              : 'Direct Scheduler Admin: Not assigned',
            inheritedAdminNames.length
              ? `Inherited from Department: ${inheritedAdminNames.join(', ')}`
              : departmentAdminNames.length
                ? 'Department coverage is already listed as a direct duplicate'
                : 'Inherited from Department: Not assigned',
          ];

          return `${group.name} (${group.status}) · ${coverage.join(' · ')}`;
        });
        const coveredGroupCount = department.schedule_groups.filter((group) => group.effective_scheduler_admins.length > 0).length;

        return {
          id: `department-${department.id}`,
          title: department.name,
          subtitle: `${department.schedule_groups.length} Schedule Group${department.schedule_groups.length === 1 ? '' : 's'}`,
          badges: [
            {
              label: departmentAdminNames.length
                ? `${departmentAdminNames.length} Department Scheduler Admin${departmentAdminNames.length === 1 ? '' : 's'}`
                : 'No Department Scheduler Admin',
              tone: departmentAdminNames.length ? ('success' as const) : ('warning' as const),
            },
            {
              label: `${coveredGroupCount}/${department.schedule_groups.length} groups covered`,
              tone: coveredGroupCount === department.schedule_groups.length && department.schedule_groups.length > 0
                ? ('success' as const)
                : ('warning' as const),
            },
          ],
          children: [
            departmentAdminNames.length
              ? `Department Scheduler Admin: ${departmentAdminNames.join(', ')}`
              : 'Department Scheduler Admin: Not assigned',
            ...(scheduleGroups.length ? scheduleGroups : ['No Schedule Groups assigned to this Department']),
          ],
          locationNames: [],
          serviceLines: [],
          route: '/admin-settings/groups',
          actionLabel: 'Manage Department & Groups',
        };
      })
      .filter((record) => matchesSearch(loweredSearch, [record.title, record.subtitle, ...record.children]));

    const unassignedRecords: RelationshipGroup['records'] = unassignedScheduleGroups
      .map((group) => ({
        id: `unassigned-group-${group.id}`,
        title: group.name,
        subtitle: 'Unassigned Schedule Group',
        badges: [{
          label: group.direct_scheduler_admins.length
            ? `${group.direct_scheduler_admins.length} direct Scheduler Admin${group.direct_scheduler_admins.length === 1 ? '' : 's'}`
            : 'No Scheduler Admin',
          tone: group.direct_scheduler_admins.length ? ('success' as const) : ('warning' as const),
        }],
        children: [
          'Department: Unassigned',
          group.direct_scheduler_admins.length
            ? `Direct Scheduler Admin: ${group.direct_scheduler_admins.map((admin) => admin.name).join(', ')}`
            : 'Direct Scheduler Admin: Not assigned',
        ],
        locationNames: [],
        serviceLines: [],
        route: '/admin-settings/groups',
        actionLabel: 'Assign Department',
      }))
      .filter((record) => matchesSearch(loweredSearch, [record.title, record.subtitle, ...record.children]));

    if (departmentHierarchy.length > 0 || unassignedScheduleGroups.length > 0) {
      return [
        {
          id: 'departments-to-groups',
          title: 'Departments → Schedule Groups → Scheduler Admins',
          subtitle: 'Review the shared company Department hierarchy and confirm direct or inherited Scheduler Admin coverage for every Schedule Group.',
          records: canonicalHierarchyRecords,
        },
        ...(unassignedRecords.length > 0 ? [{
          id: 'unassigned-schedule-groups',
          title: 'Unassigned Schedule Groups',
          subtitle: 'Assign each Schedule Group to a Department and confirm direct Scheduler Admin coverage when needed.',
          records: unassignedRecords,
        }] : []),
      ];
    }

    const hospitalsToServiceLines: RelationshipGroup['records'] = locations
      .map((location) => {
        const locationName = location.cells.name ?? 'Unnamed location';
        const relatedServiceLines = serviceLines.filter((serviceLine) => {
          const scopes = splitValues(serviceLine.cells.locations);
          return scopes.some((scope) => normalizeValue(scope) === normalizeValue(locationName));
        });

        return {
          id: `location-${location.id}`,
          title: locationName,
          subtitle: location.cells.region || location.cells.code || 'No region saved',
          colorMeta: getRecordColorMeta('locations', location, store) ?? undefined,
          badges: [
            { label: `${relatedServiceLines.length} service line${relatedServiceLines.length === 1 ? '' : 's'}`, tone: 'primary' as const },
            {
              label: relatedServiceLines.some((row) => coverage.uncoveredForFirstDraft.includes(row.cells.name ?? '')) ? 'Readiness gap' : 'Coverage mapped',
              tone: relatedServiceLines.some((row) => coverage.uncoveredForFirstDraft.includes(row.cells.name ?? '')) ? ('warning' as const) : ('success' as const),
            },
          ],
          children: relatedServiceLines.map((row) => {
            const serviceLineName = row.cells.name ?? 'Unnamed service line';
            const assignment = schedulerAdmins.find((admin) => normalizeValue(admin.cells.serviceLine ?? '') === normalizeValue(serviceLineName));
            const scheduleCount = schedulesByServiceLine[serviceLineName]?.length ?? 0;
            const assignmentLabel = assignment ? `${assignment.cells.name} (${getSchedulerStatus(assignment)})` : 'No Scheduler Admin';
            return `${serviceLineName} · ${assignmentLabel} · ${scheduleCount} schedule${scheduleCount === 1 ? '' : 's'}`;
          }),
          locationNames: [locationName],
          serviceLines: relatedServiceLines.map((row) => row.cells.name ?? '').filter(Boolean),
          route: managementRoutes.locations,
          actionLabel: relatedServiceLines.length === 0 ? 'Add service line to this hospital' : 'Manage locations',
        };
      })
      .filter((record) =>
        recordMatchesFilters(loweredSearch, locationFilter, serviceLineFilter, [record.title, record.subtitle, ...record.children], record.locationNames, record.serviceLines)
      );

    const serviceLinesToHospitals: RelationshipGroup['records'] = serviceLines
      .map((serviceLine) => {
        const serviceLineName = serviceLine.cells.name ?? 'Unnamed service line';
        const scopedLocations = splitValues(serviceLine.cells.locations);
        return {
          id: `service-line-locations-${serviceLine.id}`,
          title: serviceLineName,
          subtitle: serviceLine.cells.scheduleType || 'Schedule type not set',
          colorMeta: getRecordColorMeta('service-lines', serviceLine, store) ?? undefined,
          badges: [
            { label: scopedLocations.length ? `${scopedLocations.length} location${scopedLocations.length === 1 ? '' : 's'}` : 'No locations', tone: scopedLocations.length ? ('primary' as const) : ('warning' as const) },
            { label: `${(schedulesByServiceLine[serviceLineName] ?? []).length} schedules`, tone: 'info' as const },
          ],
          children: scopedLocations.length ? scopedLocations : ['No locations assigned yet'],
          locationNames: scopedLocations,
          serviceLines: [serviceLineName],
          route: managementRoutes['service-lines'],
          actionLabel: scopedLocations.length ? 'Manage service line' : 'Add location coverage',
        };
      })
      .filter((record) =>
        recordMatchesFilters(loweredSearch, locationFilter, serviceLineFilter, [record.title, record.subtitle, ...record.children], record.locationNames, record.serviceLines)
      );

    const serviceLinesToSchedulers: RelationshipGroup['records'] = serviceLines
      .map((serviceLine) => {
        const serviceLineName = serviceLine.cells.name ?? 'Unnamed service line';
        const assignments = schedulerAdmins.filter((admin) => normalizeValue(admin.cells.serviceLine ?? '') === normalizeValue(serviceLineName));
        const scheduleNames = schedulesByServiceLine[serviceLineName] ?? [];
        return {
          id: `service-line-admins-${serviceLine.id}`,
          title: serviceLineName,
          subtitle: serviceLine.cells.scheduleType || 'Schedule type not set',
          colorMeta: getRecordColorMeta('service-lines', serviceLine, store) ?? undefined,
          badges: [
            { label: assignments.length ? `${assignments.length} admin assignment${assignments.length === 1 ? '' : 's'}` : 'No admins', tone: assignments.length ? ('success' as const) : ('warning' as const) },
            { label: `${scheduleNames.length} schedules`, tone: 'info' as const },
          ],
          children: assignments.length
            ? assignments.flatMap((assignment) => [
              `Scheduler Admin: ${assignment.cells.name} (${getSchedulerStatus(assignment)})`,
              ...scheduleNames.map((schedule) => `Schedule: ${schedule}`),
            ])
            : ['No Scheduler Admin assigned yet', ...scheduleNames.map((schedule) => `Schedule: ${schedule}`)],
          locationNames: splitValues(serviceLine.cells.locations),
          serviceLines: [serviceLineName],
          route: managementRoutes['scheduler-admins'],
          actionLabel: assignments.length ? 'Manage Scheduler Admins' : 'Assign Scheduler Admin',
        };
      })
      .filter((record) =>
        recordMatchesFilters(loweredSearch, locationFilter, serviceLineFilter, [record.title, record.subtitle, ...record.children], record.locationNames, record.serviceLines)
      );

    const groupsToServiceLines: RelationshipGroup['records'] = groups
      .map((group) => ({
        id: `group-${group.id}`,
        title: group.cells.name ?? 'Unnamed department',
        subtitle: group.cells.serviceLine || 'Parent service line not set',
        colorMeta: getRecordColorMeta('groups-teams', group, store) ?? undefined,
        badges: [
          { label: group.cells.location || 'No location scope', tone: group.cells.location ? ('info' as const) : ('warning' as const) },
          { label: group.cells.schedulerAdmin || 'No Scheduler Admin', tone: group.cells.schedulerAdmin ? ('success' as const) : ('warning' as const) },
        ],
        children: [
          `Parent service line: ${group.cells.serviceLine || 'Not set'}`,
          `Location scope: ${group.cells.location || 'Inherit from parent'}`,
          `Scheduler Admin: ${group.cells.schedulerAdmin || 'Not assigned'}`,
        ],
        locationNames: group.cells.location ? [group.cells.location] : [],
        serviceLines: group.cells.serviceLine ? [group.cells.serviceLine] : [],
        route: managementRoutes['groups-teams'],
        actionLabel: 'Manage departments',
      }))
      .filter((record) =>
        recordMatchesFilters(loweredSearch, locationFilter, serviceLineFilter, [record.title, record.subtitle, ...record.children], record.locationNames, record.serviceLines)
      );

    return [
      {
        id: 'hospitals-to-service-lines',
        title: 'Hospitals → Service Lines',
        subtitle: 'Department by hospital/location to review service coverage and downstream readiness.',
        records: hospitalsToServiceLines,
      },
      {
        id: 'service-lines-to-hospitals',
        title: 'Service Lines → Hospitals',
        subtitle: 'Confirm each service line is mapped to the right operating sites.',
        records: serviceLinesToHospitals,
      },
      {
        id: 'service-lines-to-schedulers',
        title: 'Service Lines → Scheduler Admins → Schedules',
        subtitle: 'Review operational ownership, invite state, and the current schedule rows for each service line.',
        records: serviceLinesToSchedulers,
      },
      {
        id: 'groups-to-service-lines',
        title: 'Departments → Service Lines',
        subtitle: 'Inspect optional subgroups and the service-line context they inherit.',
        records: groupsToServiceLines,
      },
    ];
  }, [coverage.uncoveredForFirstDraft, departmentHierarchy, groups, locationFilter, locations, loweredSearch, schedulesByServiceLine, schedulerAdmins, serviceLineFilter, serviceLines, unassignedScheduleGroups]);

  const tableSections = useMemo(
    () => [
      {
        id: 'locations',
        title: 'Hospitals / Locations',
        route: managementRoutes.locations,
        count: locations.length,
        summary: 'Add, edit, reorder, import, and deactivate locations while keeping service-line dependencies visible.',
        warningCount: readinessItems.filter((item) => item.route === managementRoutes.locations && item.tone !== 'info' && item.tone !== 'success').length,
      },
      {
        id: 'service-lines',
        title: 'Service Lines',
        route: managementRoutes['service-lines'],
        count: serviceLines.length,
        summary: 'Maintain the central relationship layer across schedule type, locations, Scheduler Admin ownership, and live schedule rows.',
        warningCount: readinessItems.filter((item) => item.route === managementRoutes['service-lines'] && item.tone !== 'info' && item.tone !== 'success').length,
      },
      {
        id: 'groups-teams',
        title: 'Departments',
        route: managementRoutes['groups-teams'],
        count: groups.length,
        summary: 'Keep optional departments scoped to service lines and locations only when the company truly needs them.',
        warningCount: readinessItems.filter((item) => item.route === managementRoutes['groups-teams'] && item.tone !== 'info' && item.tone !== 'success').length,
      },
      {
        id: 'scheduler-admins',
        title: 'Scheduler Admin Assignments',
        route: managementRoutes['scheduler-admins'],
        count: schedulerAdmins.length,
        summary: 'Track invite lifecycle, assignment scope, and the difference between activation coverage and first-draft readiness.',
        warningCount: readinessItems.filter((item) => item.route === managementRoutes['scheduler-admins'] && item.tone !== 'info' && item.tone !== 'success').length,
      },
    ].filter((section) =>
      matchesSearch(loweredSearch, [section.title, section.summary])
        && (serviceLineFilter === 'all' || section.id !== 'locations')
    ),
    [groups.length, loweredSearch, locations.length, readinessItems, schedulerAdmins.length, serviceLineFilter, serviceLines.length]
  );

  const readinessBuckets = useMemo(() => {
    const filtered = readinessItems.filter((item) => {
      if (readinessFilter !== 'all' && item.tone !== readinessFilter) {
        return false;
      }

      return recordMatchesFilters(loweredSearch, locationFilter, serviceLineFilter, [item.title, item.body], item.locationNames, item.serviceLines);
    });

    return {
      critical: filtered.filter((item) => item.tone === 'critical'),
      warning: filtered.filter((item) => item.tone === 'warning'),
      info: filtered.filter((item) => item.tone === 'info'),
      success: filtered.filter((item) => item.tone === 'success'),
    };
  }, [locationFilter, loweredSearch, readinessFilter, readinessItems, serviceLineFilter]);

  const openDetail = (record: DetailRecord) => setDetailRecord(record);

  return (
    <>
      <Card className="custom-card">
        <Card.Header className="p-3">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <div className="card-title">Department &amp; Scheduler Coverage</div>
              <p className="text-muted mb-0">
                Review the shared company Department to Schedule Group hierarchy and direct or inherited Scheduler Admin coverage.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {(hasCanonicalHierarchy ? (['relationships', 'readiness'] as ViewMode[]) : (Object.keys(viewModeLabels) as ViewMode[])).map((mode) => (
                <Button key={mode} size="sm" variant={viewMode === mode ? 'primary' : 'outline-primary'} onClick={() => setViewMode(mode)}>
                  {viewModeLabels[mode]}
                </Button>
              ))}
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-3">

          {loading ? (
            <Alert variant="info" className="mb-4">
              Loading Company Admin setup views.
            </Alert>
          ) : null}

          {requestError ? (
            <Alert variant="danger" className="mb-4">
              {requestError}
            </Alert>
          ) : null}

          <Row className="g-2 mb-3 align-items-end">
            <Col md={8} xl={6}>
              <Form.Group>
                <Form.Label className="mb-1">Search visible data</Form.Label>
                <Form.Control
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder={hasCanonicalHierarchy ? 'Search Departments, Schedule Groups, or Scheduler Admins' : 'Search hospitals, service lines, admins, or readiness text'}
                />
              </Form.Group>
            </Col>
            {!hasCanonicalHierarchy ? <Col md={3} xl={2}>
              <Form.Group>
                <Form.Label className="mb-1">Location</Form.Label>
                <Form.Select value={locationFilter} onChange={(event) => setLocationFilter(event.target.value)}>
                  <option value="all">All locations</option>
                  {locationOptions.map((location) => (
                    <option key={location} value={normalizeValue(location)}>
                      {location}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col> : null}
            {!hasCanonicalHierarchy ? <Col md={3} xl={2}>
              <Form.Group>
                <Form.Label className="mb-1">Service line</Form.Label>
                <Form.Select value={serviceLineFilter} onChange={(event) => setServiceLineFilter(event.target.value)}>
                  <option value="all">All service lines</option>
                  {serviceLineOptions.map((serviceLine) => (
                    <option key={serviceLine} value={normalizeValue(serviceLine)}>
                      {serviceLine}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col> : null}
            <Col md={4} xl={3}>
              <Form.Group>
                <Form.Label className="mb-1">Readiness</Form.Label>
                <Form.Select value={readinessFilter} onChange={(event) => setReadinessFilter(event.target.value)}>
                  <option value="all">All signals</option>
                  <option value="critical">Critical blockers</option>
                  <option value="warning">Warnings</option>
                  <option value="info">Informational</option>
                  <option value="success">Ready</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={12} xl={3}>
              <div className="d-flex justify-content-md-end h-100 align-items-end">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    setSearchValue('');
                    setLocationFilter('all');
                    setServiceLineFilter('all');
                    setReadinessFilter('all');
                  }}
                >
                  Reset filters
                </Button>
              </div>
            </Col>
          </Row>

          {viewMode === 'overview' ? (
            <>
              <Row className="g-3 mb-4">
                {overviewCards.map((metric) => (
                  <Col sm={6} xl={4} key={metric.label}>
                    <Card className="custom-card border shadow-none mb-0 h-100">
                      <Card.Body>
                        <p className="text-muted fs-12 text-uppercase mb-2">{metric.label}</p>
                        <h4 className="mb-0">{metric.value}</h4>
                        <Badge bg="" className={`${toneBadgeClassMap[metric.tone]} mt-3`}>
                          {metric.tone}
                        </Badge>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Row className="g-3">
                <Col xxl={6}>
                  <Card className="custom-card h-100">
                    <Card.Header>
                      <div className="card-title">Hospitals / Locations</div>
                    </Card.Header>
                    <Card.Body>
                      <ListGroup variant="flush">
                        {locations
                          .filter((row) =>
                            recordMatchesFilters(
                              loweredSearch,
                              locationFilter,
                              serviceLineFilter,
                              [row.cells.name ?? '', row.cells.code ?? '', row.cells.region ?? '', row.cells.serviceLines ?? ''],
                              [row.cells.name ?? ''],
                              splitValues(row.cells.serviceLines)
                            )
                          )
                          .map((row) => (
                            <ListGroup.Item key={row.id} className="px-0">
                              {(() => {
                                const colorMeta = getRecordColorMeta('locations', row, store);
                                return (
                              <div className="d-flex justify-content-between align-items-start gap-3">
                                <div>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
                                    onClick={() => openDetail({
                                      title: row.cells.name ?? 'Location',
                                      subtitle: row.cells.region || row.cells.code || 'Location summary',
                                      lines: [
                                        `Code: ${row.cells.code || 'Not set'}`,
                                        `Status: ${row.status}`,
                                        `Service lines: ${row.cells.serviceLines || 'None yet'}`,
                                      ],
                                      route: managementRoutes.locations,
                                      actionLabel: 'Manage locations',
                                    })}
                                  >
                                    {row.cells.name ?? 'Unnamed location'}
                                  </button>
                                  <p className="text-muted mb-0">{row.cells.code || 'No code'} · {row.cells.region || 'Region missing'}</p>
                                  {colorMeta ? (
                                    <div className="mt-2">
                                      <Badge
                                        bg=""
                                        className="border"
                                        style={{ backgroundColor: colorMeta.hex ?? '#f8f9fa', color: colorMeta.hex ? colorMeta.textColor : '#6c757d' }}
                                      >
                                        {colorMeta.hex ?? 'No color'} · {colorMeta.label}
                                      </Badge>
                                    </div>
                                  ) : null}
                                </div>
                                <Badge bg="" className={row.cells.serviceLines ? toneBadgeClassMap.primary : toneBadgeClassMap.warning}>
                                  {splitValues(row.cells.serviceLines).length} service lines
                                </Badge>
                              </div>
                                );
                              })()}
                            </ListGroup.Item>
                          ))}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xxl={6}>
                  <Card className="custom-card h-100">
                    <Card.Header>
                      <div className="card-title">Service Lines</div>
                    </Card.Header>
                    <Card.Body>
                      <ListGroup variant="flush">
                        {serviceLines
                          .filter((row) =>
                            recordMatchesFilters(
                              loweredSearch,
                              locationFilter,
                              serviceLineFilter,
                              [row.cells.name ?? '', row.cells.scheduleType ?? '', row.cells.locations ?? '', row.cells.schedulerAdmin ?? ''],
                              splitValues(row.cells.locations),
                              [row.cells.name ?? '']
                            )
                          )
                          .map((row) => {
                            const serviceLineName = row.cells.name ?? 'Service line';
                            const scheduleCount = schedulesByServiceLine[serviceLineName]?.length ?? 0;
                            const readinessGap = coverage.uncoveredForFirstDraft.includes(serviceLineName);
                            const colorMeta = getRecordColorMeta('service-lines', row, store);
                            return (
                              <ListGroup.Item key={row.id} className="px-0">
                                <div className="d-flex justify-content-between align-items-start gap-3">
                                  <div>
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
                                      onClick={() => openDetail({
                                        title: serviceLineName,
                                        subtitle: row.cells.scheduleType || 'Schedule type not set',
                                        lines: [
                                          `Locations: ${row.cells.locations || 'None yet'}`,
                                          `Scheduler Admin: ${row.cells.schedulerAdmin || 'Unassigned in service-line row'}`,
                                          `Schedules planned: ${scheduleCount}`,
                                        ],
                                        route: managementRoutes['service-lines'],
                                        actionLabel: readinessGap ? 'Fix blocker' : 'Manage service line',
                                      })}
                                    >
                                      {serviceLineName}
                                    </button>
                                    <p className="text-muted mb-0">{row.cells.locations || 'No locations yet'}</p>
                                    {colorMeta ? (
                                      <div className="mt-2">
                                        <Badge
                                          bg=""
                                          className="border"
                                          style={{ backgroundColor: colorMeta.hex ?? '#f8f9fa', color: colorMeta.hex ? colorMeta.textColor : '#6c757d' }}
                                        >
                                          {colorMeta.hex ?? 'Mixed'} · {colorMeta.label}
                                        </Badge>
                                      </div>
                                    ) : null}
                                  </div>
                                  <Badge bg="" className={readinessGap ? toneBadgeClassMap.warning : toneBadgeClassMap.success}>
                                    {scheduleCount} schedules
                                  </Badge>
                                </div>
                              </ListGroup.Item>
                            );
                          })}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xxl={6}>
                  <Card className="custom-card h-100">
                    <Card.Header>
                      <div className="card-title">Departments</div>
                    </Card.Header>
                    <Card.Body>
                      <ListGroup variant="flush">
                        {groups
                          .filter((row) =>
                            recordMatchesFilters(
                              loweredSearch,
                              locationFilter,
                              serviceLineFilter,
                              [row.cells.name ?? '', row.cells.serviceLine ?? '', row.cells.location ?? '', row.cells.schedulerAdmin ?? ''],
                              row.cells.location ? [row.cells.location] : [],
                              row.cells.serviceLine ? [row.cells.serviceLine] : []
                            )
                          )
                          .map((row) => {
                            const colorMeta = getRecordColorMeta('groups-teams', row, store);
                            return (
                            <ListGroup.Item key={row.id} className="px-0">
                              <div className="d-flex justify-content-between align-items-start gap-3">
                                <div>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
                                    onClick={() => openDetail({
                                      title: row.cells.name ?? 'Department',
                                      subtitle: row.cells.serviceLine || 'Parent service line not set',
                                      lines: [
                                        `Location scope: ${row.cells.location || 'Inherited from parent'}`,
                                        `Scheduler Admin: ${row.cells.schedulerAdmin || 'Not assigned'}`,
                                        `Status: ${row.status}`,
                                      ],
                                      route: managementRoutes['groups-teams'],
                                      actionLabel: 'Manage departments',
                                    })}
                                  >
                                    {row.cells.name ?? 'Unnamed department'}
                                  </button>
                                  <p className="text-muted mb-0">{row.cells.serviceLine || 'No parent service line'} · {row.cells.location || 'No location override'}</p>
                                  {colorMeta ? (
                                    <div className="mt-2">
                                      <Badge
                                        bg=""
                                        className="border"
                                        style={{ backgroundColor: colorMeta.hex ?? '#f8f9fa', color: colorMeta.hex ? colorMeta.textColor : '#6c757d' }}
                                      >
                                        {colorMeta.hex ?? 'Mixed'} · {colorMeta.label}
                                      </Badge>
                                    </div>
                                  ) : null}
                                </div>
                                <Badge bg="" className={row.cells.schedulerAdmin ? toneBadgeClassMap.info : toneBadgeClassMap.warning}>
                                  {row.cells.schedulerAdmin ? 'Scoped admin' : 'Needs review'}
                                </Badge>
                              </div>
                            </ListGroup.Item>
                            );
                          })}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xxl={6}>
                  <Card className="custom-card h-100">
                    <Card.Header>
                      <div className="card-title">Scheduler Admins</div>
                    </Card.Header>
                    <Card.Body>
                      <ListGroup variant="flush">
                        {schedulerAdmins
                          .filter((row) =>
                            recordMatchesFilters(
                              loweredSearch,
                              locationFilter,
                              serviceLineFilter,
                              [row.cells.name ?? '', row.cells.email ?? '', row.cells.serviceLine ?? '', row.cells.locationScope ?? '', row.cells.inviteStatus ?? ''],
                              splitValues(row.cells.locationScope),
                              row.cells.serviceLine ? [row.cells.serviceLine] : []
                            )
                          )
                          .map((row) => (
                            <ListGroup.Item key={row.id} className="px-0">
                              <div className="d-flex justify-content-between align-items-start gap-3">
                                <div>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
                                    onClick={() => openDetail({
                                      title: row.cells.name ?? 'Scheduler Admin',
                                      subtitle: row.cells.email || 'No email set',
                                      lines: [
                                        `Service line: ${row.cells.serviceLine || 'Not set'}`,
                                        `Location scope: ${row.cells.locationScope || 'All service-line locations'}`,
                                        `Invite status: ${row.cells.inviteStatus || 'Unknown'}`,
                                      ],
                                      route: managementRoutes['scheduler-admins'],
                                      actionLabel: getSchedulerStatus(row) === 'pending' ? 'Track invite' : 'Manage assignment',
                                    })}
                                  >
                                    {row.cells.name ?? 'Unnamed Scheduler Admin'}
                                  </button>
                                  <p className="text-muted mb-0">{row.cells.serviceLine || 'No service line'} · {row.cells.locationScope || 'All locations'}</p>
                                </div>
                                <Badge bg="" className={getSchedulerStatus(row) === 'active' ? toneBadgeClassMap.success : getSchedulerStatus(row) === 'pending' ? toneBadgeClassMap.warning : toneBadgeClassMap.info}>
                                  {getSchedulerStatus(row)}
                                </Badge>
                              </div>
                            </ListGroup.Item>
                          ))}
                      </ListGroup>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : null}

          {viewMode === 'table' ? (
            <Row className="g-3">
              {tableSections.map((section) => (
                <Col xxl={6} key={section.id}>
                  <Card className="custom-card h-100">
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-start gap-3">
                        <div className="card-title">{section.title}</div>
                        <Badge bg="" className={section.warningCount > 0 ? toneBadgeClassMap.warning : toneBadgeClassMap.success}>
                          {section.count} rows
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <p className="text-muted">{section.summary}</p>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <Link to={section.route} className="btn btn-sm btn-primary">
                          Open table
                        </Link>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => setViewMode('relationships')}
                        >
                          View relationships
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setViewMode('readiness')}
                        >
                          Review readiness
                        </Button>
                      </div>
                      {section.warningCount > 0 ? (
                        <Alert variant="warning" className="mb-0">
                          {section.warningCount} readiness signal{section.warningCount === 1 ? '' : 's'} map back to this section.
                        </Alert>
                      ) : (
                        <Alert variant="success" className="mb-0">
                          No current readiness alerts are pointing at this section.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : null}

          {viewMode === 'relationships' ? (
            <Accordion alwaysOpen defaultActiveKey={relationshipGroups.map((group) => group.id)}>
              {relationshipGroups.map((group) => (
                <Accordion.Item eventKey={group.id} key={group.id}>
                  <Accordion.Header>{group.title}</Accordion.Header>
                  <Accordion.Body>
                    <p className="text-muted">{group.subtitle}</p>
                    {group.records.length === 0 ? (
                      <Alert variant="light" className="border mb-0">
                        No records match the current search and filter combination.
                      </Alert>
                    ) : (
                      <ListGroup variant="flush">
                        {group.records.map((record) => (
                          <ListGroup.Item key={record.id} className="px-0">
                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                              <div>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
                                  onClick={() => openDetail({
                                    title: record.title,
                                    subtitle: record.subtitle,
                                    lines: record.children,
                                    route: record.route,
                                    actionLabel: record.actionLabel,
                                  })}
                                >
                                  {record.title}
                                </button>
                                <p className="text-muted mb-0">{record.subtitle}</p>
                                {record.colorMeta ? (
                                  <div className="mt-2">
                                    <Badge
                                      bg=""
                                      className="border"
                                      style={{
                                        backgroundColor: record.colorMeta.hex ?? '#f8f9fa',
                                        color: record.colorMeta.hex ? record.colorMeta.textColor : '#6c757d',
                                      }}
                                    >
                                      {record.colorMeta.hex ?? 'Mixed'} · {record.colorMeta.label}
                                    </Badge>
                                  </div>
                                ) : null}
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                {record.badges.map((badge) => (
                                  <Badge bg="" className={toneBadgeClassMap[badge.tone]} key={`${record.id}-${badge.label}`}>
                                    {badge.label}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <ListGroup variant="flush">
                              {record.children.map((child) => (
                                <ListGroup.Item key={`${record.id}-${child}`} className="px-0 py-1 border-0 text-muted">
                                  {child}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                            <div className="mt-3">
                              <Link to={record.route} className="btn btn-sm btn-outline-primary">
                                {record.actionLabel}
                              </Link>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          ) : null}

          {viewMode === 'readiness' && hasCanonicalHierarchy ? (
            <Row className="g-2">
              <Col xl={12}>
                <div className="border rounded p-3 d-flex flex-wrap gap-4 justify-content-between">
                  {[
                    ['Departments', canonicalCoverage.departmentCount, `${canonicalCoverage.departmentAdminCount} with Scheduler Admin`],
                    ['Schedule Groups', canonicalCoverage.scheduleGroupCount, `${canonicalCoverage.coveredGroupCount} covered`],
                    ['Coverage gaps', canonicalCoverage.uncoveredGroups.length, 'Without effective access'],
                    ['Unassigned Groups', canonicalCoverage.unassignedGroupCount, 'Without a Department'],
                  ].map(([label, value, detail]) => (
                    <div key={label} className="flex-grow-1">
                      <p className="text-muted fs-12 text-uppercase mb-1">{label}</p>
                      <div className="d-flex align-items-baseline gap-2"><h4 className="mb-0">{value}</h4><span className="text-muted fs-12">{detail}</span></div>
                    </div>
                  ))}
                </div>
              </Col>
              <Col xl={12}>
                {canonicalCoverage.uncoveredGroups.length > 0 ? (
                  <Alert variant="warning" className="mb-0">
                    <div className="fw-semibold mb-1">Scheduler Admin coverage required</div>
                    <div>{canonicalCoverage.uncoveredGroups.map((group) => group.name).join(', ')} {canonicalCoverage.uncoveredGroups.length === 1 ? 'does' : 'do'} not have a direct Scheduler Admin or inherited Department assignment.</div>
                  </Alert>
                ) : (
                  <Alert variant="success" className="mb-0">Every assigned Schedule Group has direct or inherited Scheduler Admin coverage.</Alert>
                )}
              </Col>
            </Row>
          ) : null}

          {viewMode === 'readiness' && !hasCanonicalHierarchy ? (
            <Row className="g-3">
              <Col xl={4}>
                <Card className="custom-card h-100">
                  <Card.Header>
                    <div className="card-title">Readiness Summary</div>
                  </Card.Header>
                  <Card.Body>
                    <ListGroup variant="flush">
                      <ListGroup.Item className="px-0 d-flex justify-content-between gap-3">
                        <span className="text-muted">Activation coverage</span>
                        <span>{coverage.companyActivationCoveredCount}/{coverage.activeServiceLineCount}</span>
                      </ListGroup.Item>
                      <ListGroup.Item className="px-0 d-flex justify-content-between gap-3">
                        <span className="text-muted">First-draft readiness</span>
                        <span>{coverage.firstDraftReadyCount}/{coverage.activeServiceLineCount}</span>
                      </ListGroup.Item>
                      <ListGroup.Item className="px-0 d-flex justify-content-between gap-3">
                        <span className="text-muted">Critical blockers</span>
                        <span>{readinessBuckets.critical.length}</span>
                      </ListGroup.Item>
                      <ListGroup.Item className="px-0 d-flex justify-content-between gap-3">
                        <span className="text-muted">Warnings</span>
                        <span>{readinessBuckets.warning.length}</span>
                      </ListGroup.Item>
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
              <Col xl={8}>
                {([
                  { key: 'critical', title: 'Critical blockers', tone: 'critical' as const, items: readinessBuckets.critical },
                  { key: 'warning', title: 'Warnings', tone: 'warning' as const, items: readinessBuckets.warning },
                  { key: 'info', title: 'Informational gaps', tone: 'info' as const, items: readinessBuckets.info },
                  { key: 'success', title: 'Ready signals', tone: 'success' as const, items: readinessBuckets.success },
                ]).map((bucket) => (
                  <Card className="custom-card mb-3" key={bucket.key}>
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center gap-3">
                        <div className="card-title">{bucket.title}</div>
                        <Badge bg="" className={toneBadgeClassMap[bucket.tone]}>
                          {bucket.items.length}
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {bucket.items.length === 0 ? (
                        <p className="text-muted mb-0">No items match the current filters.</p>
                      ) : (
                        <ListGroup variant="flush">
                          {bucket.items.map((item) => (
                            <ListGroup.Item key={item.id} className="px-0">
                              <div className="d-flex justify-content-between align-items-start gap-3">
                                <div>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
                                    onClick={() => openDetail({
                                      title: item.title,
                                      subtitle: bucket.title,
                                      lines: [item.body, `Route: ${item.route}`],
                                      route: item.route,
                                      actionLabel: item.actionLabel,
                                    })}
                                  >
                                    {item.title}
                                  </button>
                                  <p className="text-muted mb-0">{item.body}</p>
                                </div>
                                <Link to={item.route} className="btn btn-sm btn-outline-primary">
                                  {item.actionLabel}
                                </Link>
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </Col>
            </Row>
          ) : null}
        </Card.Body>
      </Card>

      <Offcanvas show={detailRecord !== null} onHide={() => setDetailRecord(null)} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{detailRecord?.title}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {detailRecord ? (
            <>
              <p className="text-muted">{detailRecord.subtitle}</p>
              <ListGroup variant="flush" className="mb-4">
                {detailRecord.lines.map((line) => (
                  <ListGroup.Item key={`${detailRecord.title}-${line}`} className="px-0">
                    {line}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <Link to={detailRecord.route} className="btn btn-primary" onClick={() => setDetailRecord(null)}>
                {detailRecord.actionLabel}
              </Link>
            </>
          ) : null}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default DynamicViewsWorkspace;
